import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import MeasuringPDF from "../../pdf/OCS - Order Preparation Form.pdf";
import { Link } from "react-router-dom";
import Item from "./ValanceItem";
import { ValanceOptions } from "../shop/data";
import ContactBlock from "../../components/contact/ContactBlock";

const MeasuringGuideScreen = () => {
	// Animation constants matching home page
	const bounceTransition = {
		duration: 0.4,
		ease: [0.68, -0.25, 0.265, 1.25],
	};

	const valanceKeys = Object.keys(ValanceOptions);

	const sections = [
		{
			title: "Mounting Considerations",
			bullets: true,
			content: {
				columns: [
					{
						title: "Inside Mount",
						image: "/images/windows/guide/mount_inside.png",
						list: [
							"Inside mounting will give you the cleanest, integrated look",
							"Allows oyu to hang drapes/curtains over the window shade",
							"You will have a modest light gap on either side of the shade",
							'Minimum 2" mounting depth required for inside mount installation',
							"Window frame must be deep enough to accommodate chosen valance/cassette",
							"Window obstructions (handles, locks, etc.) may impact shade orientation",
							"Ensure frame is square (measure diagonals)",
						],
					},
					{
						title: "Outside Mount",
						image: "/images/windows/guide/mount_outside.png",
						list: [
							"More valance options, especially when your window frame is not deep enough for mounting",
							"No light gap as the shade will cover the window completely",
							"Provides fuller coverage and light blockage, allowing for more darkness when you need it",
							"Can make windows appear larger",
							"Ideal for shallow window frames",
							"Helps hide window imperfections",
						],
					},
				],
			},
		},
		{
			title: "Valance Considerations",
			bullets: true,
			content: {
				columns: [
					{
						list: [
							"Determine where you will mount the shade, making sure you have at least 2 inches of flat space to install.",
							<span key="valance-note">
								When deciding on your valance option, make sure your mounting
								depth can accommodate the valance/cassette (
								<span className="font-semibold text-primary hover:text-secondary transition-colors">
									see valance types section for sizes
								</span>
								) or decide if you are ok with the valance protruding outside
								the window frame.
							</span>,
						],
					},
					{
						list: [
							"For outside mount installations, add 4-6 inches to overall width for optimal light blockage",
						],
					},
				],
			},
		},
		{
			title: "Valance Types",
			bullets: false,
			content: {
				columns: [
					{
						list: valanceKeys
							.slice(0, Math.ceil(valanceKeys.length / 2))
							.map((key) => <Item key={key} data={ValanceOptions[key]} />),
					},
					{
						list: valanceKeys
							.slice(Math.ceil(valanceKeys.length / 2))
							.map((key) => <Item key={key} data={ValanceOptions[key]} />),
					},
				],
			},
		},
		{
			title: "Width Measurements",
			bullets: true,
			content: {
				columns: [
					{
						list: [
							"Measure to the nearest 1⁄8 inch at three points: top, middle, and bottom",
							"Use the smallest measurement for inside mount",
							"Check for any protruding hardware",
						],
					},
					{
						list: [
							"Add 4-6 inches to window width for outside mount",
							"Consider overlap for optimal light blocking for outside mount",
							"Account for any architectural features",
						],
					},
				],
			},
		},
		{
			title: "Height Measurements",
			bullets: true,
			content: {
				columns: [
					{
						list: [
							"Measure to the nearest 1⁄8 inch from inside surface at top of window to top of sill at three points",
							"Use the smallest measurement for a precise fit",
							"Measure from intended mounting height to desired endpoint",
						],
					},
					{
						list: [
							"Add 3-4 inches above window frame for mounting hardware",
							"Consider extending below the window sill for complete coverage",
						],
					},
				],
			},
		},
	];

	return (
		<div className="overflow-x-hidden bg-gradient-to-b from-white to-gray-50">
			<Helmet>
				<title>
					How to Measure Windows For Shades: Blinds Measuring Guide | Off Cut
					Shades
				</title>
				<meta
					name="description"
					content="Learn how to measure windows for shades and blinds. Off Cut Shades provides a complete blind measuring guide for our custom shade solutions."
				/>
				<link
					rel="canonical"
					href="https://offcutshades.com/help/measuring-guide"
				/>
			</Helmet>

			<main className="container mx-auto px-4 py-12">
				{/* Hero Section */}
				<motion.div
					className="text-center mb-16"
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={bounceTransition}
				>
					<motion.div
						className="flex justify-center mb-6"
						initial={{ scale: 0 }}
						animate={{ scale: 1 }}
						transition={{ ...bounceTransition, delay: 0.2 }}
					>
						<img
							src="/images/newHome/Measuring-Guide-Logo-80x80.png"
							alt="Measuring Guide"
							className="w-20 h-20"
						/>
					</motion.div>

					<motion.h1
						className="relative z-10 text-5xl md:text-7xl font-bold mb-6 leading-tight"
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ ...bounceTransition, delay: 0.3 }}
					>
						How to Measure Your{" "}
						<span className="relative">
							<span className="text-primary">Windows</span>
						</span>
					</motion.h1>
				</motion.div>

				{/* PDF Download Section */}
				<motion.div
					className="bg-white rounded-3xl shadow-lg p-8 mb-16"
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ ...bounceTransition, delay: 0.4 }}
				>
					<div className="text-center max-w-2xl mx-auto">
						<h2 className="text-2xl font-bold mb-4">Measuring Worksheet</h2>
						<p className="text-gray-600 mb-6">
							Download our measuring worksheet to organize your precise
							measurements.
						</p>
						<motion.a
							href={MeasuringPDF}
							target="_blank"
							className="inline-flex items-center px-8 py-4 bg-primary text-white rounded-xl font-semibold shadow-lg hover:bg-primary/90 transition-colors"
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
						>
							Download Measuring Worksheet
						</motion.a>
					</div>
				</motion.div>

				{/* Measurement Sections */}
				<div className="space-y-16">
					{sections.map((section, index) => (
						<motion.section
							key={index}
							className="bg-white rounded-3xl shadow-lg overflow-hidden"
							initial={{ opacity: 0, y: 50 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: index * 0.1 }}
							viewport={{ once: true }}
						>
							<div className="p-8">
								<div className="flex items-center gap-4 mb-6">
									<img
										src="/images/icons/checked.png"
										alt="check"
										className="w-6 h-6"
									/>
									<h2 className="text-2xl font-bold">{section.title}</h2>
								</div>

								<div className="grid md:grid-cols-2 gap-8">
									{section.content.columns.map((column, colIndex) => (
										<div key={colIndex} className="space-y-6">
											{column.image && (
												<img
													src={column.image}
													alt={column.title}
													className="w-full rounded-xl shadow-md"
												/>
											)}
											{column.title && (
												<h3 className="text-xl font-semibold">
													{column.title}
												</h3>
											)}
											<ul className="flex flex-col space-y-4 items-start justify-baseline">
												{column.list.map((item, itemIndex) => (
													<li
														key={itemIndex}
														className="flex items-start gap-3"
													>
														{section.bullets && (
															<div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
														)}
														<span className="text-gray-600">{item}</span>
													</li>
												))}
											</ul>
										</div>
									))}
								</div>
							</div>
						</motion.section>
					))}
				</div>
			</main>
			{/* Contact Support Section */}
			<ContactBlock />
		</div>
	);
};

export default MeasuringGuideScreen;
